import logo from "@public/logo.svg"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import PaperPlaneIcon from "@/assets/icons/settings/paper-plane-icon.tsx"
import LoadingCircle from "@/components/loading-animation/loading-circle.tsx"
import PageContentWrapperLarge from "@/components/page-content-wrappers/page-content-wrapper-large.tsx"
import { Button } from "@/components/shadcn/button.tsx"
import MobileTopBar from "@/components/top-bars/mobile-top-bar/mobile-top-bar.tsx"
import {
  useCreateAssistantMutation,
  useGetAllAssistantThreadsForUserQuery,
  useWarmUpMutation,
} from "@/features/assistant/api/assistant-api.ts"
import { AssistantDrawer } from "@/features/assistant/components/assistant-drawer/assistant-drawer.tsx"
import RecentThreads from "@/features/assistant/components/assistant-page-components/recent-threads.tsx"
import SuggestedQuestions from "@/features/assistant/components/assistant-page-components/suggested-questions.tsx"
import { LimitWidget } from "@/features/assistant/components/limit-widget/limit-widget.tsx"
import { AI_ASSISTANT_VIEW } from "@/features/assistant/routes/route-list.ts"
import { type SuggestedQuestion } from "@/features/assistant/types/assistant.ts"
import { useGetWelcomeMessage } from "@/features/home/hooks/use-get-welcome-message.ts"
import { HOME_SCREEN } from "@/features/home/routes/route-list.ts"
import { useGetProfileQuery } from "@/features/settings-account/api/account-api.ts"
import { initialUserProfile } from "@/features/settings-account/utils/initialStates.ts"
import {
  WHITE,
  MENU_VOICE_ASSISTANT_COLOR,
} from "@/utils/constants/colors-constants.ts"
import { cn } from "@/utils/styles.ts"

const AssistantPage = () => {
  const { t } = useTranslation()
  const [trigger] = useWarmUpMutation()
  const { data: userProfile = initialUserProfile } = useGetProfileQuery()
  const { data: threadList, isLoading } = useGetAllAssistantThreadsForUserQuery(
    undefined,
    { refetchOnMountOrArgChange: true },
  )
  const welcomeMessage = useGetWelcomeMessage(userProfile?.first_name)
  const [createThread, { isLoading: isLoadingCreateThread }] =
    useCreateAssistantMutation()
  const navigate = useNavigate()

  useEffect(() => {
    // Warm up cache once when component mounts
    trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionally empty - run once on mount

  const [message, setMessage] = useState("")

  const handleSendMessage = () => {
    if (message.length > 0) {
      createThread({
        title: message.split("\n")[0].substring(0, 50),
      })
        .unwrap()
        .then((thread) => {
          navigate(`${AI_ASSISTANT_VIEW}/${thread.id}`, {
            state: { initialMessage: message },
          })
        })
    }
  }

  const handleSuggestionsClick = useCallback(
    (suggestion: SuggestedQuestion) => {
      createThread({
        title: t(suggestion.question_text).split("\n")[0].substring(0, 50),
      })
        .unwrap()
        .then((thread) => {
          navigate(`${AI_ASSISTANT_VIEW}/${thread.id}`, {
            state: {
              initialMessage: t(suggestion.question_text),
              suggested_question_id: suggestion.id,
            },
          })
        })
    },
    [createThread, navigate, t],
  )

  const handleStartVocalAssistant = () => {
    createThread({ title: t("voiceAssistant") })
      .unwrap()
      .then((thread) => {
        navigate(`${AI_ASSISTANT_VIEW}/${thread.id}`, {
          state: { voiceMode: true },
        })
      })
  }

  const showLimitExceeded =
    !isLoading &&
    threadList &&
    threadList?.rate_limit_info.messages_remaining === 0
  const showRemainingMessages =
    threadList &&
    !isLoading &&
    threadList?.rate_limit_info.messages_remaining < 10 &&
    threadList?.rate_limit_info.messages_remaining > 0

  return (
    <>
      <MobileTopBar
        title={t("intelligentAssistant")}
        icon={<AssistantDrawer />}
        onIconClick={() => {}}
        onBackClick={() => navigate(HOME_SCREEN)}
      />
      <PageContentWrapperLarge className={"flex flex-col items-center gap-4"}>
        <div className={"my-3 flex flex-col items-center gap-3"}>
          <img src={logo} alt={"logo"} className={"h-12 w-12"} />
          <p
            className={
              "max-w-full text-center align-middle text-2xl font-medium text-zinc-600 max-md:line-clamp-2 max-md:break-words md:overflow-hidden md:overflow-ellipsis"
            }
          >
            {welcomeMessage}
          </p>
        </div>
        {showLimitExceeded && (
          <LimitWidget reset_date={threadList.rate_limit_info.reset_date} />
        )}
        {showRemainingMessages && (
          <LimitWidget
            remaining={threadList.rate_limit_info.messages_remaining}
          />
        )}
        <div className={"grid w-full grid-rows-[auto_40px_auto] gap-4"}>
          <textarea
            disabled={threadList?.rate_limit_info.messages_remaining === 0}
            className={
              "h-[120px] w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-[14px] ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brown-500 focus-visible:ring-offset-2"
            }
            placeholder={t("whatCanIHelpWith")}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <Button
            onClick={handleSendMessage}
            variant={"default"}
            size={"lg"}
            className={cn(
              "flex items-center justify-center gap-2 rounded-[6px] bg-gray-300 text-[14px] font-medium text-white",
              message.length > 0 && "bg-brown-500",
            )}
          >
            {isLoadingCreateThread ? (
              <LoadingCircle className={"h-5 w-5 border-2 text-white"} />
            ) : (
              <>
                {t("startChat")}
                <PaperPlaneIcon
                  color={WHITE}
                  size={20}
                  className={"mr-[3px]"}
                />
              </>
            )}
          </Button>
          <Button
            onClick={handleStartVocalAssistant}
            variant={"default"}
            size={"lg"}
            className={cn(
              "flex items-center justify-center gap-2 rounded-[6px] text-[14px] font-medium",
              "text-white",
              "border-none",
              "shadow-md",
              "transition-colors duration-200",
              "hover:opacity-90",
              "focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2",
            )}
            style={{ backgroundColor: MENU_VOICE_ASSISTANT_COLOR }}
          >
            {t("voiceAssistant")}
          </Button>
        </div>
        <SuggestedQuestions onQuestionClick={handleSuggestionsClick} />
        <RecentThreads />
      </PageContentWrapperLarge>
    </>
  )
}

export default AssistantPage
